import { Component, DestroyRef, EventEmitter, forwardRef, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs';
import { ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';
import { GbrDocumentsService } from '@coin/shared/gbr/data-access';
import { GbrFile } from '@coin/shared/gbr/util';
import { Fn } from '@coin/shared/core/util-models';

@Component({
  selector: 'coin-siemens-energy-file-input',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class FileInputComponent implements ControlValueAccessor {
  private gbrFileService = inject(GbrDocumentsService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  @Output() delete = new EventEmitter<void>();

  public disabled = false;

  private file: GbrFile = null;

  set value(val: GbrFile) {
    this.file = val;
    this.onChange(val);
  }

  get value(): GbrFile {
    return this.file;
  }

  onChange: Fn = () => {};
  onTouch: Fn = () => {};

  public requestUpload(fileInput: HTMLInputElement): void {
    ConfirmationDialogComponent.openDialog(this.dialog, {
      headline: 'gbr.upload-confirmation-headline',
      msg: 'gbr.upload-confirmation-body',
      confirmMsg: 'general.confirm',
      cancelMsg: 'general.cancel',
      translate: true
    })
      .pipe(
        filter(result => !!result),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => fileInput.click());
  }

  public handleFiles(files: FileList): void {
    const file: File = files['0'];
    if (!file) {
      return;
    }

    const gbrFile: GbrFile = {
      file,
      fileName: file.name,
      fileType: file.type,
      sizeInBytes: file.size
    };

    this.writeValue(gbrFile);
    this.onTouch(gbrFile);
  }

  public remove(): void {
    this.delete.emit();
  }

  public getFileSize(file: GbrFile): string {
    return this.bytesToSize(file.sizeInBytes);
  }

  private bytesToSize(bytes: number): string {
    if (!bytes && bytes !== 0) {
      return '';
    }
    if (bytes === 0) {
      return '0 Byte';
    }

    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / 1024 ** i)} ${sizes[i]}`;
  }

  public getFileType(file: GbrFile): string {
    const nameSplit = file.fileName.split('.');
    return nameSplit ? nameSplit[nameSplit.length - 1] : 'pdf';
  }

  public download(): void {
    if (!this.file.filePath) {
      return;
    }

    this.gbrFileService.downloadGbrDocument(this.file.filePath).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public writeValue(value: GbrFile): void {
    this.value = value;
  }

  public registerOnChange(fn: Fn): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: Fn): void {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
